/**
 * Utility functions for tag management
 */
import { useState } from 'react';

/**
 * Toggle a tag in an array
 * @param {string} tag - The tag to toggle
 * @param {Array<string>} currentTags - Current array of tags
 * @returns {Array<string>} - New array with tag toggled
 */
export const toggleTag = (tag, currentTags) => {
  if (currentTags.includes(tag)) {
    // Remove if already selected
    return currentTags.filter(item => item !== tag);
  } else {
    // Add if not selected
    return [...currentTags, tag];
  }
};

/**
 * Toggle a tag in a nested filter object
 * @param {string} tag - The tag to toggle
 * @param {Object} activeFilters - The filter object with tags property
 * @returns {Object} - New filter object with updated tags
 */
export const toggleFilterTag = (tag, activeFilters) => {
  const isTagActive = activeFilters.tags.includes(tag);
  
  if (isTagActive) {
    // Remove tag if already active
    return {
      ...activeFilters,
      tags: activeFilters.tags.filter(t => t !== tag)
    };
  } else {
    // Add tag if not active
    return {
      ...activeFilters,
      tags: [...activeFilters.tags, tag]
    };
  }
};

/**
 * Custom hook for managing tag filters
 * @param {Object} initialFilters - Initial filter state
 * @returns {Object} - Filter state and functions
 */
export const useTagFilters = (initialFilters = { tags: [] }) => {
  const [activeFilters, setActiveFilters] = useState(initialFilters);
  
  const toggleTag = (tag) => {
    setActiveFilters(prev => toggleFilterTag(tag, prev));
  };
  
  return { 
    activeFilters, 
    setActiveFilters, 
    toggleTag,
    hasActiveFilters: activeFilters.tags.length > 0
  };
};

/**
 * Custom hook for managing simple tag selection
 * @param {Array<string>} initialTags - Initial selected tags
 * @param {Function} onChange - Optional callback when tags change
 * @returns {Object} - Selected tags state and functions
 */
export const useSelectedTags = (initialTags = [], onChange = null) => {
  const [selectedTags, setSelectedTags] = useState(initialTags);
  
  // Update selected tags and trigger callback if provided
  const setTagsWithCallback = (newTags) => {
    setSelectedTags(newTags);
    if (onChange) {
      onChange(newTags);
    }
  };
  
  // Toggle a tag in the selected tags
  const toggleTagSelection = (tag) => {
    setTagsWithCallback(prevTags => toggleTag(tag, prevTags));
  };
  
  // Clear all selected tags
  const clearTags = () => {
    setTagsWithCallback([]);
  };
  
  return {
    selectedTags,
    setSelectedTags: setTagsWithCallback,
    toggleTagSelection,
    clearTags,
    hasSelectedTags: selectedTags.length > 0
  };
};

/**
 * Extracts all unique tags from an array of thoughts
 * @param {Array} thoughts - Array of thought objects
 * @returns {Array} Array of unique tag strings
 */
export const getAllUniqueTags = (thoughts) => {
  if (!thoughts || !Array.isArray(thoughts)) return [];
  
  // Create a Set to store unique tags
  const uniqueTags = new Set();
  
  // Iterate through thoughts and add tags to the Set
  thoughts.forEach(thought => {
    if (thought.tags && Array.isArray(thought.tags)) {
      thought.tags.forEach(tag => uniqueTags.add(tag));
    }
  });
  
  // Convert Set to Array and return
  return Array.from(uniqueTags);
};

/**
 * Calculate popular tags from an array of thoughts
 * @param {Array} thoughts - Array of thought objects
 * @param {number} limit - Maximum number of tags to return (default: 50)
 * @returns {Array} Array of objects with { tag, count } sorted by count in descending order
 */
export const getPopularTags = (thoughts, limit = 50) => {
  // Extract and count all tags
  const tagCounts = {};
  thoughts.forEach(thought => {
    if (thought.tags && Array.isArray(thought.tags)) {
      thought.tags.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    }
  });
  
  // Convert to array and sort by count (descending)
  return Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([tag, count]) => ({ tag, count }))
    .slice(0, limit);
};

/**
 * Extracts all unique intentions from an array of thoughts
 * @param {Array} thoughts - Array of thought objects
 * @returns {Array} Array of unique intention strings
 */
export const getAllUniqueIntentions = (thoughts) => {
  if (!thoughts || !Array.isArray(thoughts)) return [];
  
  // Create a Set to store unique intentions
  const uniqueIntentions = new Set();
  
  // Iterate through thoughts and add intentions to the Set
  thoughts.forEach(thought => {
    if (thought.intention && typeof thought.intention === 'string' && thought.intention.trim()) {
      uniqueIntentions.add(thought.intention.trim());
    }
  });
  
  // Convert Set to Array and return
  return Array.from(uniqueIntentions);
}; 